import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProduct, getProducts } from '../api/api';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useAuth } from '../context/AuthContext';
import ProductReviews from '../components/ProductReviews';
import { hasSale, getSalePrice, getSavingsAmount, withEffectivePrice } from '../utils/sale';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { favorites, addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const { isShopper } = useAuth();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchProduct = async () => {
    try {
      const response = await getProduct(id);
      const product = response.data.data || response.data;
      setProduct(product);
      if (Array.isArray(product.colors) && product.colors.length > 0) {
        setSelectedColor(product.colors[0]);
      }
      
      // Fetch related products from same category
      if (product.category) {
        fetchRelatedProducts(product.category, product._id);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (category, currentProductId) => {
    try {
      const response = await getProducts({ category, limit: 5 });
      // Filter out current product and unpublished products, limit to 4 related items
      const related = (response.data.products || response.data || [])
        .filter(p => p._id !== currentProductId && p.isPublished !== false)
        .slice(0, 4);
      setRelatedProducts(related);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  };

  const handleAddToCart = () => {
    addToCart(withEffectivePrice(product), quantity, selectedColor);
    alert('Product added to cart!');
  };

  const handleFavoriteClick = () => {
    if (!isShopper()) {
      setShowLoginPrompt(true);
      return;
    }

    if (isFavorite(product._id)) {
      removeFromFavorites(product._id);
    } else {
      addToFavorites(product);
    }
  };

  if (loading) return <div className="container"><p>Loading...</p></div>;
  if (!product) return <div className="container"><p>Product not found</p></div>;

  // Safety check for images array
  const images = product.images && Array.isArray(product.images) ? product.images : ['https://picsum.photos/seed/placeholder/500/500'];
  const currentSelectedImage = Math.min(selectedImage, images.length - 1);

  // Helper function to get image URL
  const getImageUrl = (image) => {
    if (!image) return 'https://picsum.photos/seed/placeholder/500/500';
    const url = typeof image === 'string' ? image : image.url;
    if (!url) return 'https://picsum.photos/seed/placeholder/500/500';
    return url.startsWith('http') ? url : `${window.location.origin}/.netlify/functions/api${url}`;
  };

  return (
    <div className="product-detail container">
      <button onClick={() => navigate('/products')} className="back-btn">← Back to Products</button>
      
      <div className="product-detail-content">
        <div className="product-images">
          <img 
            src={getImageUrl(images[currentSelectedImage])} 
            alt={product.name}
            className="main-image"
          />
          {images.length > 1 && (
            <div className="thumbnail-list">
              {images.map((img, index) => (
                <img
                  key={index}
                  src={getImageUrl(img)}
                  alt={`${product.name} ${index + 1}`}
                  className={`thumbnail ${currentSelectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="product-info">          <div className="product-header">
            <div>
              <h1>{product.name}</h1>
              {product.sku && (
                <p className="product-sku">SKU: {product.sku}</p>
              )}
            </div>
            <button 
              className={`favorite-btn ${isFavorite(product._id) ? 'favorited' : ''}`}
              onClick={handleFavoriteClick}
              title={isFavorite(product._id) ? 'Remove from favorites' : 'Add to favorites'}
            >
              <span className="heart-icon">♡</span>
            </button>
          </div>

          <p className="price">
            {hasSale(product) ? (
              <>
                <span className="price--original">R{(product.price || 0).toFixed(2)}</span>
                {' '}
                <span className="price--sale">R{getSalePrice(product).toFixed(2)}</span>
                {' '}
                <span className="sale-badge">SALE {product.salePercentage}% OFF</span>
              </>
            ) : (
              `R${(product.price || 0).toFixed(2)}`
            )}
          </p>
          {hasSale(product) && (
            <p className="sale-savings">You save R{getSavingsAmount(product).toFixed(2)}</p>
          )}
          <p className="culture-badge">{product.culturalOrigin || product.culture} Culture | {product.category}</p>
          
          <div className="description">
            <h3>Description</h3>
            <p>{product.description}</p>
          </div>

          {product.vendor && (
            <div className="vendor-info">
              <h4>Vendor: {product.vendor.name}</h4>
            </div>
          )}

          <div className="stock-info">
            {product.stock > 0 ? (
              <p className="in-stock">In Stock: {product.stock} available</p>
            ) : (
              <p className="out-of-stock">Out of Stock</p>
            )}
          </div>

          {product.stock > 0 && (
            <div className="purchase-section">
              {Array.isArray(product.colors) && product.colors.length > 0 ? (
                <div className="color-selector">
                  <label>Colour:</label>
                  <select
                    value={selectedColor}
                    onChange={(e) => setSelectedColor(e.target.value)}
                  >
                    {product.colors.map((color) => (
                      <option key={color} value={color}>{color}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="color-selector-default">Colour: Default</p>
              )}

              <div className="quantity-selector">
                <label>Quantity:</label>
                {isMobile ? (
                  <select
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                  >
                    {Array.from({ length: product.stock }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="number"
                    min="1"
                    max={product.stock}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock, parseInt(e.target.value) || 1)))}
                  />
                )}
              </div>
              
              <button onClick={handleAddToCart} className="add-to-cart-btn">
                Add to Cart
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Customer Reviews */}
      <ProductReviews productId={product._id} />

      {/* Related Products Section */}
      {relatedProducts.length > 0 && (
        <div className="related-products">
          <h2>You May Also Like</h2>
          <div className="related-products-grid">
            {relatedProducts.map(relatedProduct => (
              <div 
                key={relatedProduct._id} 
                className="related-product-card"
                onClick={() => navigate(`/products/${relatedProduct._id}`)}
              >
                <img 
                  src={getImageUrl(relatedProduct.images[0])} 
                  alt={relatedProduct.name}
                />
                <h4>{relatedProduct.name}</h4>
                <p className="related-price">R{(relatedProduct.price || 0).toFixed(2)}</p>
                {relatedProduct.sku && (
                  <p className="related-sku">SKU: {relatedProduct.sku}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="modal-overlay" onClick={() => setShowLoginPrompt(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLoginPrompt(false)}>✕</button>
            <div className="modal-body">
              <h2>❤️ Save Your Favorites</h2>
              <p>Create an account to save products and track your orders!</p>
              <div className="modal-buttons">
                <button 
                  className="modal-btn signup-btn"
                  onClick={() => {
                    setShowLoginPrompt(false);
                    navigate('/login');
                  }}
                >
                  Create Account
                </button>
                <button 
                  className="modal-btn login-btn"
                  onClick={() => {
                    setShowLoginPrompt(false);
                    navigate('/login');
                  }}
                >
                  Sign In
                </button>
              </div>
              <p className="modal-subtext">Already logged in? <button className="link-btn" onClick={() => setShowLoginPrompt(false)}>Continue Shopping</button></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetail;
